'use client';

import { ArrowRight } from 'lucide-react';

import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

interface VoiceLink {
  slug: string;
  title: string;
  description: string;
}

const USE_CASES: VoiceLink[] = [
  {
    slug: 'ai-receptionist',
    title: 'AI Receptionist',
    description:
      'Picks up every call on the first ring, books, qualifies, and transfers warmly.',
  },
  {
    slug: 'ai-call-center',
    title: 'AI Call Center',
    description:
      'Inbound and outbound at scale, behind your existing CCaaS or PBX.',
  },
  {
    slug: 'ai-answering-service',
    title: 'AI Answering Service',
    description:
      '24/7 coverage that resolves the call instead of just taking a message.',
  },
  {
    slug: 'ai-voice-agent',
    title: 'AI Voice Agent',
    description:
      'A production agent on your existing carrier and CRM in days, not quarters.',
  },
  {
    slug: 'ai-after-hours-answering',
    title: 'After-Hours Answering',
    description:
      'AI absorbs the routine; pages a human only when the call is genuinely urgent.',
  },
  {
    slug: 'ai-appointment-booking',
    title: 'AI Appointment Booking',
    description:
      'Books in your live calendar before hangup. Reschedules and cancellations included.',
  },
  {
    slug: 'ai-outbound-calling',
    title: 'AI Outbound Calling',
    description:
      'Callbacks, payment reminders, win-back, surveys — at dialer scale, with real conversations.',
  },
  {
    slug: 'ai-cold-caller',
    title: 'AI Cold Caller',
    description:
      'Volume + quality. Discovery, qualification, and live calendar booking.',
  },
  {
    slug: 'replace-ivr-with-ai',
    title: 'Replace IVR with AI',
    description:
      'Caller talks; AI listens, classifies, resolves, or routes. No menu maze.',
  },
  {
    slug: 'ai-voicemail',
    title: 'AI Voicemail',
    description:
      'Voicemails become tasks: transcribed, classified, and routed automatically.',
  },
  {
    slug: 'voice-ai-platform',
    title: 'Voice AI Platform',
    description:
      'SIP, tools, observability, compliance — six layers, one product.',
  },
  {
    slug: 'ai-phone-calls',
    title: 'AI Phone Calls',
    description:
      'Real PSTN calls with sub-200ms reply latency in 100+ languages.',
  },
];

const REGIONS: VoiceLink[] = [
  {
    slug: 'ai-receptionist-uk',
    title: 'AI Receptionist (UK)',
    description:
      'BT, Gamma, Vonage UK. UK-GDPR aligned. OFCOM-compliant outbound.',
  },
  {
    slug: 'ai-receptionist-australia',
    title: 'AI Receptionist (Australia)',
    description: 'Telstra, Optus, MyNetFone. Privacy Act 1988. ACMA-aware.',
  },
  {
    slug: 'ai-receptionist-canada',
    title: 'AI Receptionist (Canada)',
    description:
      'Bell, Telus, Rogers. Bilingual EN + québécois. PIPEDA + Law 25.',
  },
  {
    slug: 'ai-receptionist-india',
    title: 'AI Receptionist (India)',
    description:
      'Knowlarity, Exotel, MyOperator. 10+ Indian languages. DPDP Act 2023.',
  },
  {
    slug: 'ai-answering-service-uk',
    title: 'AI Answering Service (UK)',
    description:
      'Resolves calls instead of taking messages. UK-GDPR, OFCOM, 24/7.',
  },
  {
    slug: 'ai-answering-service-australia',
    title: 'AI Answering Service (Australia)',
    description:
      'Australian English, AEST/AWST timezone routing, Privacy Act aligned.',
  },
  {
    slug: 'ai-voice-agent-uk',
    title: 'AI Voice Agent (UK)',
    description: 'Production-ready voice AI for UK businesses. Days to live.',
  },
  {
    slug: 'ai-voice-agent-australia',
    title: 'AI Voice Agent (Australia)',
    description:
      'Production-ready voice AI for Australian businesses. AU English.',
  },
  {
    slug: 'ai-voice-agent-canada',
    title: 'AI Voice Agent (Canada)',
    description:
      'Bilingual production voice AI. Sydney AWS Canadian region available.',
  },
];

const LANGUAGES: VoiceLink[] = [
  {
    slug: 'ki-telefonassistent',
    title: 'KI-Telefonassistent (Deutsch)',
    description: 'KI-Telefonassistent für deutsche Unternehmen. DSGVO-konform.',
  },
  {
    slug: 'ki-sprachagent',
    title: 'KI-Sprachagent (Deutsch)',
    description: 'Produktionsreifer Sprachagent für deutsche Telefonanlagen.',
  },
  {
    slug: 'ki-kundenservice',
    title: 'KI im Kundenservice (Deutsch)',
    description:
      'Standardanfragen direkt im Gespräch lösen — DSGVO, EU-Region.',
  },
  {
    slug: 'ai-klantenservice',
    title: 'AI voor klantenservice (Nederlands)',
    description:
      'AI die uw klantenservice draagt — AVG-conform, EU-datahosting.',
  },
  {
    slug: 'ai-telefoonassistent',
    title: 'AI-telefoonassistent (Nederlands)',
    description: 'AI-receptie voor Nederlandse en Belgische bedrijven.',
  },
  {
    slug: 'agent-vocal-ia',
    title: 'Agent vocal IA (Français)',
    description:
      'Agent vocal IA pour entreprises françaises. RGPD, hébergement UE.',
  },
  {
    slug: 'standardiste-ia',
    title: 'Standardiste IA (Français)',
    description: 'Standardiste IA qui décroche en moins de 2 secondes — RGPD.',
  },
  {
    slug: 'agente-de-voz-ia',
    title: 'Agente de voz IA (Español)',
    description: 'Agente de voz IA para empresas españolas. RGPD, hosting UE.',
  },
  {
    slug: 'recepcionista-ia',
    title: 'Recepcionista IA (Español)',
    description: 'Recepcionista IA que descuelga al primer tono — RGPD.',
  },
  {
    slug: 'centralino-virtuale-ia',
    title: 'Centralino virtuale IA (Italiano)',
    description:
      'Centralino virtuale IA per aziende italiane. GDPR, hosting UE.',
  },
  {
    slug: 'atendimento-ia',
    title: 'Atendimento ao cliente com IA (Português)',
    description:
      'Atendimento por IA para empresas brasileiras — LGPD, São Paulo.',
  },
];

const INDUSTRIES: VoiceLink[] = [
  {
    slug: 'for-real-estate',
    title: 'Real Estate',
    description:
      'Inbound inquiries, showing bookings, lead follow-up — through your CRM.',
  },
  {
    slug: 'for-healthcare',
    title: 'Healthcare',
    description:
      'HIPAA-ready receptionist for clinics, group practices, and health systems.',
  },
  {
    slug: 'for-medical-clinics',
    title: 'Medical Clinics',
    description:
      'Books in Athena, Epic, Cerner, eCW. After-hours triage to on-call.',
  },
  {
    slug: 'for-dental',
    title: 'Dental',
    description:
      'OpenDental and Dentrix native. Recall, reactivation, and emergency triage.',
  },
  {
    slug: 'for-restaurants',
    title: 'Restaurants',
    description:
      'Reservations, takeout, FAQ — without tying up the host stand.',
  },
  {
    slug: 'for-legal',
    title: 'Law Firms',
    description:
      'Standardized intake, conflict checks, consultation booking — UPL-safe.',
  },
  {
    slug: 'for-law-firms',
    title: 'Law Firms (Detailed)',
    description:
      'Native Clio, MyCase, Smokeball, Filevine. Conflict checks live during call.',
  },
  {
    slug: 'for-hvac',
    title: 'HVAC',
    description:
      'ServiceTitan, Housecall Pro, Jobber. Rate-card pricing. Emergency triage.',
  },
  {
    slug: 'for-plumbers',
    title: 'Plumbers',
    description:
      'Trip charge quoted live. Emergency triage. After-hours premium pricing.',
  },
  {
    slug: 'for-contractors',
    title: 'Contractors',
    description:
      'Estimate booking, qualification, stale-quote follow-up automation.',
  },
  {
    slug: 'for-home-services',
    title: 'Home Services',
    description:
      'HVAC, plumbing, electrical, roofing, landscaping in one product.',
  },
  {
    slug: 'for-small-business',
    title: 'Small Business',
    description:
      'Same-day setup. Per-resolution pricing. Works with what you have.',
  },
  {
    slug: 'for-salons',
    title: 'Salons & Spas',
    description:
      'Mindbody, Boulevard, Vagaro. Day-of confirmation cuts no-shows 60%+.',
  },
  {
    slug: 'for-auto-dealers',
    title: 'Auto Dealerships',
    description:
      'CDK, Reynolds, DealerSocket, Tekion. Sales + service in one product.',
  },
  {
    slug: 'for-property-management',
    title: 'Property Management',
    description:
      'Tenant inquiries, maintenance, rent reminders — through your PMS.',
  },
];

const COMPARISONS: VoiceLink[] = [
  {
    slug: 'vs-vapi',
    title: 'vs. Vapi',
    description: 'Platform vs. product — when each fits.',
  },
  {
    slug: 'vs-bland',
    title: 'vs. Bland AI',
    description: 'Developer infrastructure vs. business product.',
  },
  {
    slug: 'vs-retell',
    title: 'vs. Retell AI',
    description: 'Build the agent vs. configure one.',
  },
  {
    slug: 'vs-synthflow',
    title: 'vs. Synthflow',
    description: 'No-code SMB tier vs. production-grade product.',
  },
  {
    slug: 'vs-polyai',
    title: 'vs. PolyAI',
    description: 'Self-serve product vs. managed service build.',
  },
  {
    slug: 'vs-livekit',
    title: 'vs. LiveKit',
    description: 'WebRTC infrastructure vs. productized voice agent.',
  },
  {
    slug: 'vs-elevenlabs',
    title: 'vs. ElevenLabs',
    description: 'Best TTS + developer kit vs. finished agent product.',
  },
  {
    slug: 'vs-air-ai',
    title: 'vs. Air AI',
    description: 'Outbound demo focus vs. production deployment.',
  },
  {
    slug: 'vs-smith-ai',
    title: 'vs. Smith.ai',
    description: 'Human + AI hybrid vs. fully-AI agent.',
  },
  {
    slug: 'vs-twilio',
    title: 'vs. Twilio',
    description: 'Telephony layer vs. agent runtime on top.',
  },
];

const USE_CASES_AR: VoiceLink[] = [
  {
    slug: 'ai-receptionist',
    title: 'موظف استقبال بالـ AI',
    description: 'يرد من أول رنة، يحجز، يفرز، ويحوّل ومعه السياق كامل.',
  },
  {
    slug: 'ai-call-center',
    title: 'مركز اتصال بالـ AI',
    description: 'وارد وصادر بأي حجم، خلف الـ CCaaS أو الـ PBX اللي عندك.',
  },
  {
    slug: 'ai-answering-service',
    title: 'خدمة رد آلي بالـ AI',
    description: 'تغطية ٢٤/٧ تحل المكالمة، مو بس تسجّل رسالة.',
  },
  {
    slug: 'ai-voice-agent',
    title: 'وكيل صوتي بالـ AI',
    description:
      'وكيل جاهز للإنتاج على المشغّل والـ CRM اللي عندك — بأيام، مو بأرباع سنة.',
  },
  {
    slug: 'ai-after-hours-answering',
    title: 'رد بعد الدوام',
    description:
      'الـ AI يمتص الروتيني، ولا يتصل بأحد إلا إذا كانت المكالمة عاجلة فعلاً.',
  },
  {
    slug: 'ai-appointment-booking',
    title: 'حجز مواعيد بالـ AI',
    description:
      'يحجز في تقويمك المباشر قبل ما تُنهي المكالمة. والتأجيل والإلغاء معها.',
  },
  {
    slug: 'ai-outbound-calling',
    title: 'مكالمات صادرة بالـ AI',
    description:
      'معاودة اتصال، تذكير دفع، استرجاع عملاء، استبيانات — بحجم نظام الاتصال الآلي، وبمحادثة حقيقية.',
  },
  {
    slug: 'ai-cold-caller',
    title: 'اتصال بارد بالـ AI',
    description: 'حجم + جودة. اكتشاف، فرز، وحجز مباشر في التقويم.',
  },
  {
    slug: 'replace-ivr-with-ai',
    title: 'استبدل الـ IVR بالـ AI',
    description:
      'المتصل يتكلم، والـ AI يسمع ويصنّف ويحل أو يوجّه. بدون متاهة «اضغط ١».',
  },
  {
    slug: 'ai-voicemail',
    title: 'بريد صوتي بالـ AI',
    description:
      'البريد الصوتي يتحوّل إلى مهمة: يُفرَّغ نصاً، ويُصنَّف، ويُوجَّه تلقائياً.',
  },
  {
    slug: 'voice-ai-platform',
    title: 'منصة الصوت بالـ AI',
    description: 'SIP، أدوات، مراقبة، التزام — ست طبقات، منتج واحد.',
  },
  {
    slug: 'ai-phone-calls',
    title: 'مكالمات هاتفية بالـ AI',
    description:
      'مكالمات PSTN حقيقية، رد بأقل من ٢٠٠ مللي ثانية، بأكثر من ١٠٠ لغة.',
  },
];

const REGIONS_AR: VoiceLink[] = [
  {
    slug: 'ai-receptionist-uk',
    title: 'موظف استقبال بالـ AI (UK)',
    description:
      'BT، Gamma، Vonage UK. يتوافق مع UK-GDPR، ومكالمات صادرة على قواعد OFCOM.',
  },
  {
    slug: 'ai-receptionist-australia',
    title: 'موظف استقبال بالـ AI (Australia)',
    description: 'Telstra، Optus، MyNetFone. Privacy Act 1988. متوافق مع ACMA.',
  },
  {
    slug: 'ai-receptionist-canada',
    title: 'موظف استقبال بالـ AI (Canada)',
    description:
      'Bell، Telus، Rogers. ثنائي اللغة EN + québécois. PIPEDA + Law 25.',
  },
  {
    slug: 'ai-receptionist-india',
    title: 'موظف استقبال بالـ AI (India)',
    description:
      'Knowlarity، Exotel، MyOperator. أكثر من ١٠ لغات هندية. DPDP Act 2023.',
  },
  {
    slug: 'ai-answering-service-uk',
    title: 'خدمة رد آلي بالـ AI (UK)',
    description: 'يحل المكالمة، مو بس يسجّل رسالة. UK-GDPR، OFCOM، ٢٤/٧.',
  },
  {
    slug: 'ai-answering-service-australia',
    title: 'خدمة رد آلي بالـ AI (Australia)',
    description:
      'إنجليزي أسترالي، توجيه حسب توقيت AEST/AWST، متوافق مع Privacy Act.',
  },
  {
    slug: 'ai-voice-agent-uk',
    title: 'وكيل صوتي بالـ AI (UK)',
    description: 'صوت AI جاهز للإنتاج لشركات UK. أيام ويعمل مباشرةً.',
  },
  {
    slug: 'ai-voice-agent-australia',
    title: 'وكيل صوتي بالـ AI (Australia)',
    description: 'صوت AI جاهز للإنتاج للشركات الأسترالية. إنجليزي AU.',
  },
  {
    slug: 'ai-voice-agent-canada',
    title: 'وكيل صوتي بالـ AI (Canada)',
    description:
      'صوت AI ثنائي اللغة جاهز للإنتاج. منطقة AWS الكندية Sydney متوفرة.',
  },
];

const LANGUAGES_AR: VoiceLink[] = [
  {
    slug: 'ki-telefonassistent',
    title: 'KI-Telefonassistent (Deutsch)',
    description: 'مساعد هاتفي بالـ AI للشركات الألمانية. متوافق مع DSGVO.',
  },
  {
    slug: 'ki-sprachagent',
    title: 'KI-Sprachagent (Deutsch)',
    description: 'وكيل صوتي جاهز للإنتاج لأنظمة الهاتف الألمانية.',
  },
  {
    slug: 'ki-kundenservice',
    title: 'KI im Kundenservice (Deutsch)',
    description: 'يحل الطلبات الاعتيادية مباشرة في المكالمة — DSGVO، منطقة EU.',
  },
  {
    slug: 'ai-klantenservice',
    title: 'AI voor klantenservice (Nederlands)',
    description: 'AI يتولّى دعم عملائك — متوافق مع AVG، استضافة بيانات في EU.',
  },
  {
    slug: 'ai-telefoonassistent',
    title: 'AI-telefoonassistent (Nederlands)',
    description: 'استقبال بالـ AI للشركات الهولندية والبلجيكية.',
  },
  {
    slug: 'agent-vocal-ia',
    title: 'Agent vocal IA (Français)',
    description: 'وكيل صوتي بالـ AI للشركات الفرنسية. RGPD، استضافة في UE.',
  },
  {
    slug: 'standardiste-ia',
    title: 'Standardiste IA (Français)',
    description: 'استقبال بالـ AI يرد في أقل من ثانيتين — RGPD.',
  },
  {
    slug: 'agente-de-voz-ia',
    title: 'Agente de voz IA (Español)',
    description: 'وكيل صوتي بالـ AI للشركات الإسبانية. RGPD، استضافة UE.',
  },
  {
    slug: 'recepcionista-ia',
    title: 'Recepcionista IA (Español)',
    description: 'استقبال بالـ AI يرد من أول رنة — RGPD.',
  },
  {
    slug: 'centralino-virtuale-ia',
    title: 'Centralino virtuale IA (Italiano)',
    description: 'سنترال افتراضي بالـ AI للشركات الإيطالية. GDPR، استضافة UE.',
  },
  {
    slug: 'atendimento-ia',
    title: 'Atendimento ao cliente com IA (Português)',
    description: 'دعم عملاء بالـ AI للشركات البرازيلية — LGPD، São Paulo.',
  },
];

const INDUSTRIES_AR: VoiceLink[] = [
  {
    slug: 'for-real-estate',
    title: 'العقارات',
    description:
      'استفسارات واردة، مواعيد معاينة، متابعة العملاء — كلها في الـ CRM اللي عندك.',
  },
  {
    slug: 'for-healthcare',
    title: 'الرعاية الصحية',
    description:
      'موظف استقبال جاهز لـ HIPAA للعيادات والمجمعات والأنظمة الصحية.',
  },
  {
    slug: 'for-medical-clinics',
    title: 'العيادات الطبية',
    description:
      'يحجز في Athena، Epic، Cerner، eCW. فرز بعد الدوام للطبيب المناوب.',
  },
  {
    slug: 'for-dental',
    title: 'الأسنان',
    description:
      'يدعم OpenDental وDentrix أصلياً. تذكير المراجعة، إعادة تنشيط، وفرز الطوارئ.',
  },
  {
    slug: 'for-restaurants',
    title: 'المطاعم',
    description: 'حجوزات، طلبات سفري، أسئلة شائعة — وهاتف المضيف يبقى متاحاً.',
  },
  {
    slug: 'for-legal',
    title: 'مكاتب المحاماة',
    description:
      'استقبال موحّد، فحص تعارض المصالح، حجز الاستشارات — آمن من ناحية UPL.',
  },
  {
    slug: 'for-law-firms',
    title: 'مكاتب المحاماة (تفصيلي)',
    description:
      'يدعم Clio، MyCase، Smokeball، Filevine أصلياً. فحص التعارض مباشر أثناء المكالمة.',
  },
  {
    slug: 'for-hvac',
    title: 'التكييف والتبريد',
    description:
      'ServiceTitan، Housecall Pro، Jobber. تسعير حسب القائمة. فرز طوارئ.',
  },
  {
    slug: 'for-plumbers',
    title: 'السباكة',
    description:
      'رسوم الزيارة تُذكر على الخط. فرز طوارئ. تسعير أعلى بعد الدوام.',
  },
  {
    slug: 'for-contractors',
    title: 'المقاولون',
    description: 'حجز زيارات تقدير، فرز، ومتابعة تلقائية للعروض اللي بردت.',
  },
  {
    slug: 'for-home-services',
    title: 'الخدمات المنزلية',
    description: 'تكييف، سباكة، كهرباء، أسقف، وتنسيق حدائق — في منتج واحد.',
  },
  {
    slug: 'for-small-business',
    title: 'الأعمال الصغيرة',
    description: 'تفعيل بنفس اليوم. تسعير لكل حل. يشتغل مع اللي عندك.',
  },
  {
    slug: 'for-salons',
    title: 'الصالونات والسبا',
    description:
      'Mindbody، Boulevard، Vagaro. تأكيد يوم الموعد يقلّل الغياب أكثر من ٦٠٪.',
  },
  {
    slug: 'for-auto-dealers',
    title: 'وكالات السيارات',
    description:
      'CDK، Reynolds، DealerSocket، Tekion. مبيعات + صيانة في منتج واحد.',
  },
  {
    slug: 'for-property-management',
    title: 'إدارة الأملاك',
    description:
      'استفسارات المستأجرين، طلبات الصيانة، تذكير الإيجار — من داخل الـ PMS اللي عندك.',
  },
];

const COMPARISONS_AR: VoiceLink[] = [
  {
    slug: 'vs-vapi',
    title: 'مقابل Vapi',
    description: 'منصة مقابل منتج — متى يناسبك كل واحد.',
  },
  {
    slug: 'vs-bland',
    title: 'مقابل Bland AI',
    description: 'بنية تحتية للمطورين مقابل منتج جاهز للأعمال.',
  },
  {
    slug: 'vs-retell',
    title: 'مقابل Retell AI',
    description: 'تبني الوكيل بنفسك مقابل ضبط وكيل جاهز.',
  },
  {
    slug: 'vs-synthflow',
    title: 'مقابل Synthflow',
    description: 'فئة SMB بدون كود مقابل منتج جاهز للإنتاج.',
  },
  {
    slug: 'vs-polyai',
    title: 'مقابل PolyAI',
    description: 'منتج خدمة ذاتية مقابل بناء بخدمة مُدارة.',
  },
  {
    slug: 'vs-livekit',
    title: 'مقابل LiveKit',
    description: 'بنية WebRTC مقابل وكيل صوتي جاهز كمنتج.',
  },
  {
    slug: 'vs-elevenlabs',
    title: 'مقابل ElevenLabs',
    description: 'أفضل TTS + كِت للمطورين مقابل منتج وكيل مكتمل.',
  },
  {
    slug: 'vs-air-ai',
    title: 'مقابل Air AI',
    description: 'تركيز على ديمو المكالمات الصادرة مقابل نشر جاهز للإنتاج.',
  },
  {
    slug: 'vs-smith-ai',
    title: 'مقابل Smith.ai',
    description: 'هجين بشري + AI مقابل وكيل AI بالكامل.',
  },
  {
    slug: 'vs-twilio',
    title: 'مقابل Twilio',
    description: 'طبقة اتصالات مقابل تشغيل وكيل فوقها.',
  },
];

const GROUP_LABELS = {
  en: {
    useCasesEyebrow: 'Use cases',
    useCasesTitle: 'What AI voice can do for you',
    regionEyebrow: 'By region',
    regionTitle: 'Voice AI for your country',
    industryEyebrow: 'By industry',
    industryTitle: 'Voice AI tuned for your industry',
    languageEyebrow: 'By language',
    languageTitle: 'Voice AI in your language',
    comparisonEyebrow: 'Compared to',
    comparisonTitle: 'Voice AI alternatives',
  },
  ar: {
    useCasesEyebrow: 'حالات الاستخدام',
    useCasesTitle: 'وش يسوي لك الصوت بالـ AI',
    regionEyebrow: 'حسب المنطقة',
    regionTitle: 'صوت AI لبلدك',
    industryEyebrow: 'حسب المجال',
    industryTitle: 'صوت AI مضبوط لمجالك',
    languageEyebrow: 'حسب اللغة',
    languageTitle: 'صوت AI بلغتك',
    comparisonEyebrow: 'بالمقارنة مع',
    comparisonTitle: 'بدائل صوت الـ AI',
  },
} as const;

const Group = ({
  eyebrow,
  title,
  links,
}: {
  eyebrow: string;
  title: string;
  links: VoiceLink[];
}) => (
  <div>
    <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-neutral-500">
      {eyebrow}
    </p>
    <h3 className="mt-2 font-serif text-balance text-neutral-900 text-2xl md:text-3xl">
      {title}
    </h3>
    <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {links.map((link) => (
        <Link
          key={link.slug}
          href={`/voice/${link.slug}`}
          className="group flex flex-col gap-1 rounded-xl border border-neutral-200 bg-white p-5 transition-colors duration-200 hover:border-neutral-300"
        >
          <span className="flex items-center gap-1.5 font-sans text-sm font-medium text-neutral-900 md:text-base">
            {link.title}
            <ArrowRight className="h-3.5 w-3.5 -translate-x-0.5 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100" />
          </span>
          <span className="text-xs leading-relaxed text-neutral-500 md:text-sm">
            {link.description}
          </span>
        </Link>
      ))}
    </div>
  </div>
);

export const VoiceUseCaseGrid = () => {
  const { locale } = useTranslation();
  const isAr = locale === 'ar';
  const g = isAr ? GROUP_LABELS.ar : GROUP_LABELS.en;

  return (
    <section className="bg-[#fafaf9] py-20 md:py-24">
      <div className="container space-y-16">
        <Group
          eyebrow={g.useCasesEyebrow}
          title={g.useCasesTitle}
          links={isAr ? USE_CASES_AR : USE_CASES}
        />
        <Group
          eyebrow={g.regionEyebrow}
          title={g.regionTitle}
          links={isAr ? REGIONS_AR : REGIONS}
        />
        <Group
          eyebrow={g.industryEyebrow}
          title={g.industryTitle}
          links={isAr ? INDUSTRIES_AR : INDUSTRIES}
        />
        <Group
          eyebrow={g.languageEyebrow}
          title={g.languageTitle}
          links={isAr ? LANGUAGES_AR : LANGUAGES}
        />
        <Group
          eyebrow={g.comparisonEyebrow}
          title={g.comparisonTitle}
          links={isAr ? COMPARISONS_AR : COMPARISONS}
        />
      </div>
    </section>
  );
};

export default VoiceUseCaseGrid;
